let userInfo = null;
let currentToken = null;
let isHandleLogin = null;
let userInfoPromise = null;
import request from '@/utils/request';
import router from '@/router';
import { MessageBox } from 'element-ui';
import { showError, showSuccess} from '@/utils/message';
import { getProductLogoSrc } from '@/utils/productAssets';

/** iThenticate 产品 check_type；仅含该产品时无需规则同意流程 */
export const ITHENTICATE_CHECK_TYPE = '36'

/** 用户是否包含需同意规则的产品（存在非 iThenticate 产品，如 Turnitin+AI） */
export function requiresRulesAgreement(products) {
  const list = products || getCachedUserProducts()
  if (!list.length) return false
  return list.some(item => String(item.type) !== ITHENTICATE_CHECK_TYPE)
}

/** is_agree 接口可能为 number 1 或 string '1'；rulesAgreed 为本地缓存兜底 */
export function isUserRulesAgreed(user) {
  let info = user
  if (!info || !Object.keys(info).length) {
    try {
      info = JSON.parse(localStorage.getItem('userInfo') || '{}')
    } catch {
      info = {}
    }
  }
  if (info.is_agree == 1) return true
  if (info.is_agree == 0 || info.is_agree === '0') return false
  return localStorage.getItem('rulesAgreed') === 'true'
}

/** 用 userInfo.is_agree 同步 localStorage rulesAgreed（'true' | 'false'） */
export function syncRulesAgreedFromUser(user) {
  if (!user || typeof user !== 'object' || !('is_agree' in user)) return
  localStorage.setItem('rulesAgreed', user.is_agree == 1 ? 'true' : 'false')
}

export function hasCachedUserProfile(user) {
  const info = user || {}
  const hasIdentity = !!(info.name || info.uid || info.id)
  // 仅有 name 不够：旧缓存可能缺少 is_agree，会误判未同意规则
  return hasIdentity && 'is_agree' in info
}


/** 更新本地/内存中的 userInfo（如 set_user_agree 后立即同步 is_agree） */
export function updateCachedUserInfo(partial) {
  const token = localStorage.getItem('token')
  let info = {}
  if (userInfo && Object.keys(userInfo).length) {
    info = { ...userInfo }
  } else {
    try {
      info = JSON.parse(localStorage.getItem('userInfo') || '{}')
    } catch {
      info = {}
    }
  }
  const updated = { ...info, ...partial }
  userInfo = updated
  currentToken = token
  localStorage.setItem('userInfo', JSON.stringify(updated))
  syncRulesAgreedFromUser(updated)
  return updated
}

export function getUserInfo(options = {}) {
  const force = options.force === true
  const token = localStorage.getItem('token')
  // ✅ 内存命中（最优）
  if (!force && userInfo && token === currentToken) {
    return Promise.resolve(userInfo)
  }

  // ✅ 防止并发请求
  if (userInfoPromise) {
    return userInfoPromise
  }

  // 🚀 发请求
  userInfoPromise = request.post('/api/get_user_info')
    .then(res => {
      if (res.status) {
        userInfo = res.data
        currentToken = token

        localStorage.setItem('userInfo', JSON.stringify(res.data))

        syncRulesAgreedFromUser(res.data)

        return userInfo
      } else {
        return Promise.reject(res.info)
      }
    })
    .catch(err => {
      throw err
    })
    .finally(() => {
      userInfoPromise = null
    })

  return userInfoPromise
}

export function resetUser() {
  userInfo = null
  currentToken = null
  userInfoPromise = null
  localStorage.removeItem('userInfo')
  clearUserProductsCache()
}

export const NEEDS_BRAND_SETUP_KEY = 'needsBrandSetup'
export const INVITE_CODE_PENDING_KEY = 'inviteCodePending'

export function setInviteCodePending(value) {
  if (value) {
    localStorage.setItem(INVITE_CODE_PENDING_KEY, 'true')
  } else {
    localStorage.removeItem(INVITE_CODE_PENDING_KEY)
  }
}

export function needsInviteCodePending() {
  return localStorage.getItem(INVITE_CODE_PENDING_KEY) === 'true'
}

export function clearLoginFlowFlags() {
  localStorage.removeItem(NEEDS_BRAND_SETUP_KEY)
  localStorage.removeItem(INVITE_CODE_PENDING_KEY)
}

export const USER_PRODUCTS_KEY = 'userProducts'
export const ACTIVE_PRODUCT_TYPE_KEY = 'activeProductType'
export const DEFAULT_PRODUCT_THEME = 'purple'

// 接口未返回 theme 时的兜底
const LEGACY_PRODUCT_THEME_MAP = {
  '36': 'green',
  '105': 'purple'
}

let userProductsCache = null
let userProductsFetchPromise = null

export function normalizeProductTheme(theme, type) {
  const value = String(theme || '').trim().toLowerCase()
  if (/^[a-z][a-z0-9-]*$/.test(value)) {
    return value
  }
  return LEGACY_PRODUCT_THEME_MAP[String(type)] || DEFAULT_PRODUCT_THEME
}

export function parseUserProducts(data) {
  if (!data || typeof data !== 'object') return []
  return Object.values(data)
    .map(item => ({
      type: String(item.type),
      sort_id: item.sort_id != null ? String(item.sort_id) : '',
      name: item.name || '',
      introduction: item.introduction || '',
      theme: normalizeProductTheme(item.theme, item.type)
    }))
    .sort((a, b) => {
      const sortDiff = Number(a.sort_id || 0) - Number(b.sort_id || 0)
      if (sortDiff !== 0) return sortDiff
      return Number(a.type) - Number(b.type)
    })
}

export function getProductByType(type) {
  return getCachedUserProducts().find(item => item.type === String(type))
}

export function getProductTheme(type) {
  const product = getProductByType(type)
  if (product?.theme) return product.theme
  return LEGACY_PRODUCT_THEME_MAP[String(type)] || DEFAULT_PRODUCT_THEME
}

export function getProductLogo(checkType) {
  return getProductLogoSrc(checkType || getActiveCheckType())
}

export function applyProductTheme(theme) {
  const normalizedTheme = normalizeProductTheme(theme)
  localStorage.setItem('theme', normalizedTheme)
  document.body.className = `theme-${normalizedTheme}`
}

/** 首次选品牌（未配置产品）时使用默认紫色主题 */
export function applyDefaultBrandSetupTheme() {
  applyProductTheme(DEFAULT_PRODUCT_THEME)
}

export function cacheUserProducts(products) {
  userProductsCache = products
  localStorage.setItem(USER_PRODUCTS_KEY, JSON.stringify(products))
}

export function getCachedUserProducts() {
  if (userProductsCache) return userProductsCache
  try {
    const raw = localStorage.getItem(USER_PRODUCTS_KEY)
    if (raw) {
      userProductsCache = JSON.parse(raw)
      return userProductsCache
    }
  } catch (e) {
    // ignore invalid cache
  }
  return []
}

export function clearUserProductsCache() {
  userProductsCache = null
  userProductsFetchPromise = null
  localStorage.removeItem(USER_PRODUCTS_KEY)
  localStorage.removeItem(ACTIVE_PRODUCT_TYPE_KEY)
  localStorage.removeItem('active_order_type')
  localStorage.removeItem('theme')
}

function hasUserProductsCache() {
  return userProductsCache !== null || localStorage.getItem(USER_PRODUCTS_KEY) !== null
}

export async function fetchUserProducts(options = {}) {
  const force = options.force === true

  if (!force && hasUserProductsCache()) {
    return getCachedUserProducts()
  }

  if (userProductsFetchPromise) {
    return userProductsFetchPromise
  }

  userProductsFetchPromise = request.post('/api/ajax_get_user_check_types')
    .then(res => {
      const products = parseUserProducts(res?.data)
      cacheUserProducts(products)
      return products
    })
    .finally(() => {
      userProductsFetchPromise = null
    })

  return userProductsFetchPromise
}

export function getActiveCheckType() {
  const active =
    localStorage.getItem(ACTIVE_PRODUCT_TYPE_KEY) ||
    localStorage.getItem('active_order_type')
  if (active) return String(active)
  const products = getCachedUserProducts()
  return products[0]?.type || '105'
}

export function applyActiveProductTheme(productType) {
  const type = String(productType)
  const theme = getProductTheme(type)
  localStorage.setItem(ACTIVE_PRODUCT_TYPE_KEY, type)
  localStorage.setItem('active_order_type', type)
  applyProductTheme(theme)
}

export function initActiveProduct(products) {
  const list = products || getCachedUserProducts()
  if (!list.length) {
    applyDefaultBrandSetupTheme()
    return null
  }
  const saved = localStorage.getItem(ACTIVE_PRODUCT_TYPE_KEY)
  const activeType = list.some(p => p.type === saved) ? saved : list[0].type
  applyActiveProductTheme(activeType)
  return activeType
}

export async function applyUserProducts() {
  const products = await fetchUserProducts()
  initActiveProduct(products)
  return products
}

export function isEmptyUserCheckTypes(data) {
  if (!data) return true
  if (Array.isArray(data)) return data.length === 0
  if (typeof data === 'object') return Object.keys(data).length === 0
  return false
}

export function setNeedsBrandSetup(value) {
  localStorage.setItem(NEEDS_BRAND_SETUP_KEY, value ? 'true' : 'false')
}

export function needsBrandSetup() {
  return localStorage.getItem(NEEDS_BRAND_SETUP_KEY) === 'true'
}

export function isBrandSetupUnknown() {
  return localStorage.getItem(NEEDS_BRAND_SETUP_KEY) === null
}

export async function checkUserBrandSetup() {
  const products = await fetchUserProducts()
  const empty = products.length === 0
  setNeedsBrandSetup(empty)
  if (empty) {
    applyDefaultBrandSetupTheme()
  }
  return empty
}

export async function resolvePostLoginRoute(user, options = {}) {
  let needsSetup

  if (options.products) {
    needsSetup = options.products.length === 0
    setNeedsBrandSetup(needsSetup)
    if (needsSetup) {
      applyDefaultBrandSetupTheme()
    }
  } else {
    needsSetup = await checkUserBrandSetup()
    if (needsSetup) {
      applyDefaultBrandSetupTheme()
    }
  }

  if (needsSetup) {
    return { path: '/selectBrands' }
  }

  const products = options.products || getCachedUserProducts()
  if (requiresRulesAgreement(products) && !isUserRulesAgreed(user)) {
    syncRulesAgreedFromUser(user)
    return { path: '/rules', query: { fromLogin: '1' } }
  }

  if (!options.products) {
    initActiveProduct(getCachedUserProducts())
  }
  syncRulesAgreedFromUser(user)
  return { path: '/quotationHall' }
}

/** 选品牌保存后的跳转：含 Turnitin 等产品且未同意规则时进规则页 */
export function resolvePostBrandSaveRoute(user, products, options = {}) {
  const list = products || getCachedUserProducts()
  if (requiresRulesAgreement(list) && !isUserRulesAgreed(user)) {
    const query = options.fromLogin ? { fromLogin: '1' } : undefined
    return query ? { path: '/rules', query } : { path: '/rules' }
  }
  return { path: '/quotationHall' }
}

// 处理账号被禁用（account_disabled）场景，优先展示接口返回的 info
export function handleLockLogin(info) {
  if (isHandleLogin) return
  isHandleLogin = true
  import('@/utils/userStatusPoll').then(({ stopUserStatusPolling }) => {
    stopUserStatusPolling()
  })
  localStorage.removeItem('token')
  localStorage.removeItem('rulesAgreed')
  localStorage.removeItem('userInfo')
  clearLoginFlowFlags()
  clearUserProductsCache()

  const message =
    (typeof info === 'string' && info.trim()) ||
    'The current user has been restricted from snatching orders.'

  setTimeout(() => {
    MessageBox.confirm(
      message,
      '',
      {
        confirmButtonText: 'confirm',
        showCancelButton: false,
        showCloseButton: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        type: 'warning'
      }
    ).then(() => {
      window.location.href = '/login'
    })
  }, 100)
}

export function logoOut(){
  request.get('/api/logout').then(res=>{
    if(res.status){
      import('@/utils/userStatusPoll').then(({ stopUserStatusPolling }) => {
        stopUserStatusPolling()
      })
      showSuccess(res.info || 'Logout success!');
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('rulesAgreed')
      clearLoginFlowFlags()
      clearUserProductsCache()
      router.replace('/login');
    }else{
      showError(res.info);
    }
  })
}